import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  Icon,
  USER_INTERFACE_ARROW_04_RIGHT,
  GuestRating,
  Picture,
  Button,
} from '@bwoty-web/styleguide';
import { deviceWidths } from '@bwoty-web/styleguide/variables/widths';
import { getDeviceSources } from '../utils/imageUtils';
import './ImageBlockGeo.scss';

const ImageBlockGeo = ({ className, algolia = {}, texts = {}, icon, imageDomain }) => {
  const mobileImageSize = { width: 250, height: 116 };
  const desktopImageSize = { width: 322, height: 228 };
  const deviceSpecs = [
    {
      size: mobileImageSize,
      mediaString: `(max-width: ${deviceWidths.tabletMax})`,
    },
    {
      size: desktopImageSize,
      mediaString: `(min-width: ${deviceWidths.tablet})`,
    },
  ];
  const { imageRelativeUrl } = algolia;
  const imgSources = getDeviceSources(deviceSpecs, imageDomain, imageRelativeUrl, null, null, 2);

  const getAlgoliaGeo = (algoliaResult) => {
    switch (algoliaResult.type) {
      case 'resort': {
        const algoliaAreaText = algoliaResult.area ? `${algoliaResult.area}, ` : '';
        const algoliaCountryText = algoliaResult.country || '';

        return `${algoliaAreaText}${algoliaCountryText}`;
      }
      case 'area':
        return algoliaResult.country || '';
      default:
        return '';
    }
  };

  const titleGeo = algolia.name || algolia.title;
  const preambleGeo = getAlgoliaGeo(algolia);

  const limitText = (text) => {
    return text.length > 280 ? `${text.slice(0, 280)}...` : text;
  };

  const getDescription = () => {
    if (algolia && algolia.description) {
      return limitText(algolia.description);
    }
    if (algolia && algolia.metaDescription) {
      return limitText(algolia.metaDescription);
    }
    return null;
  };

  const descriptionGeo = getDescription();

  const guestRatingGeo = !Number.isNaN(algolia.rating) && {
    title: texts && texts.guestRatingResultLabel,
    value: algolia.rating,
  };

  const hotelsGeo = !Number.isNaN(algolia.noHotels) && {
    title: texts && texts.hotelResultLabel,
    value: algolia.noHotels,
  };

  return (
    <div className={classNames('search-img-block', className)}>
      <Picture
        className={`search-img-block__image ${!imgSources.length ? '--broken' : ''}`}
        sources={imgSources}
        icon={icon}
        style={!imgSources ? { maxWidth: '250px' } : {}}
      />
      <div className="search-img-block__content">
        <div className="search-img-block__maininfo">
          {titleGeo && algolia.url && (
            <h2 className="search-img-block__title">
              <a href={algolia.url}>{titleGeo}</a>
            </h2>
          )}
          {preambleGeo && <p className="search-img-block__preamble">{preambleGeo}</p>}
          {descriptionGeo && <div className="search-img-block__description">{descriptionGeo}</div>}
        </div>
        <aside>
          {guestRatingGeo.value && guestRatingGeo.title && (
            <div className="search-img-block__rating">
              <GuestRating
                rating={guestRatingGeo.value}
                className="search-img-block__rating-value"
                text={guestRatingGeo.title}
              />
            </div>
          )}
          {hotelsGeo.value && hotelsGeo.title && (
            <div className="search-img-block__hotels">
              <span className="search-img-block__hotels-value">{hotelsGeo.value}</span>
              <span className="search-img-block__hotels-title">{hotelsGeo.title}</span>
            </div>
          )}
          {texts.readMore && algolia.url && (
            <Button theme="primary" className="search-img-block__button" href={algolia.url}>
              {texts.readMore}
            </Button>
          )}
        </aside>
        <Icon className="search-img-block__arrow" path={USER_INTERFACE_ARROW_04_RIGHT} size="xs" />
      </div>
    </div>
  );
};

ImageBlockGeo.propTypes = {
  className: PropTypes.string,
  algolia: PropTypes.shape({}),
  texts: PropTypes.shape({}),
  icon: PropTypes.shape({
    path: PropTypes.string,
    size: PropTypes.string,
    sizeMobile: PropTypes.string,
  }),
  imageDomain: PropTypes.string,
};

export default ImageBlockGeo;
